import { Button, DescriptionList, FileInput } from "@ternary/ui"
import { type ReactElement, useRef, useState } from "react"
import { uploadAdminBrandingAsset } from "../api.js"
import { NavIcon } from "./nav-icons.js"

// The types admin-api stores (it answers 415 for others), and its 5 MB limit, which it answers with
// a plain 413, so the size is checked here before anything is sent.
const ACCEPT = "image/gif,image/jpeg,image/png,image/webp"
const MAX_BYTES = 5 * 1024 * 1024
const HINTS = {
  logo: "A square JPEG, PNG, GIF or WebP, up to 5 MB.",
  background: "Shown beside the signup form. Up to 5 MB.",
  "bot-avatar": "A square image for the bot in your server, up to 5 MB.",
  "bot-banner": "A wide image for the bot's profile, up to 5 MB.",
} as const
// The image's name in the button and the note: "Upload logo", "No background yet".
const NOUNS = {
  background: ["background", "Background"],
  "bot-avatar": ["bot avatar", "Bot avatar"],
  "bot-banner": ["bot banner", "Bot banner"],
  logo: ["logo", "Logo"],
} as const

/**
 * A branding image: an upload button that sends the chosen image at once, a square thumbnail of
 * the current one beside it, and a Remove button. The note beside the button names the file just
 * uploaded, or says whether there is one. `onChange` gets the uploaded image's URL, or null once
 * it is removed. An image over 5 MB is refused before it is sent. An upload error takes the
 * hint's place until the next choice. `onBusyChange` says when an upload starts and when the
 * latest one ends. Read-only (`disabled`), it shows the thumbnail and the note alone.
 */
export function ImageUploadField({
  label,
  kind,
  value,
  onChange,
  disabled = false,
  uploadsAvailable = true,
  onBusyChange,
}: {
  label: string
  kind: "logo" | "background" | "bot-avatar" | "bot-banner"
  value: string | null
  onChange(url: string | null): void
  disabled?: boolean | undefined
  /** False while asset storage is off: the file field is disabled, and Remove still works. */
  uploadsAvailable?: boolean | undefined
  onBusyChange?: ((busy: boolean) => void) | undefined
}): ReactElement {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState("")
  // The file this field uploaded, named while it is still the value; and an image URL that failed
  // to load, which shows a placeholder until the value changes.
  const [uploaded, setUploaded] = useState<{ name: string; url: string } | null>(null)
  const [brokenUrl, setBrokenUrl] = useState<string | null>(null)
  const broken = value !== null && brokenUrl === value
  const [noun, Noun] = NOUNS[kind]
  // Counts the uploads and removals, so only the latest one changes the value: the field stays
  // enabled (and focused) while an image uploads, and another choice or Remove can overtake it.
  const latest = useRef(0)

  function busy(next: boolean) {
    setUploading(next)
    onBusyChange?.(next)
  }

  function remove() {
    latest.current += 1
    if (uploading) busy(false)
    setError("")
    onChange(null)
  }

  async function upload([file]: File[]) {
    if (!file) return
    const attempt = ++latest.current
    if (file.size > MAX_BYTES) {
      // The latest choice, so an earlier upload still running is dropped.
      if (uploading) busy(false)
      setError("Images can be up to 5 MB.")
      return
    }
    busy(true)
    setError("")
    try {
      const { url } = await uploadAdminBrandingAsset(kind, file)
      if (attempt === latest.current) {
        setUploaded({ name: file.name, url })
        onChange(url)
      }
    } catch (failure) {
      if (attempt === latest.current) {
        setError(failure instanceof Error ? failure.message : "The image could not be uploaded.")
      }
    } finally {
      if (attempt === latest.current) busy(false)
    }
  }

  // The thumbnail, or a placeholder once the image fails to load.
  const tile = value ? (
    <span className="admin-upload-tile">
      {broken ? (
        <NavIcon name="image" />
      ) : (
        <img alt={`${label} preview`} onError={() => setBrokenUrl(value)} src={value} />
      )}
    </span>
  ) : null
  const note = !value
    ? `No ${noun} yet`
    : broken
      ? "Image could not load"
      : uploaded?.url === value
        ? uploaded.name
        : `${Noun} uploaded`

  if (disabled) {
    return (
      <DescriptionList
        items={[
          {
            term: label,
            value: (
              <span className="admin-upload-value">
                {tile}
                {note}
              </span>
            ),
          },
        ]}
      />
    )
  }

  return (
    <div className={value ? "admin-upload has-tile" : "admin-upload"}>
      {tile}
      <FileInput
        accept={ACCEPT}
        busy={uploading}
        buttonLabel={`${value ? "Replace" : "Upload"} ${noun}`}
        disabled={!uploadsAvailable}
        emptyLabel={note}
        error={error || undefined}
        fileName={value ? note : undefined}
        hint={uploading ? "Uploading…" : HINTS[kind]}
        label={label}
        onFiles={(files) => void upload(files)}
      />
      {value ? (
        <Button
          aria-label={`Remove the ${label.toLowerCase()}`}
          className="admin-upload-remove"
          onClick={remove}
          size="sm"
          variant="secondary"
        >
          Remove
        </Button>
      ) : null}
      {/* Mounted from the start, so the upload is announced as it begins. */}
      <span className="tny-visually-hidden" role="status">
        {uploading ? `Uploading the ${label.toLowerCase()}…` : ""}
      </span>
    </div>
  )
}
